// src/components/modals/AdoptionOfferConfirmationModal.jsx
"use client";

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './AdoptionOfferConfirmationModal.css';

const AdoptionOfferConfirmationModal = ({ 
  isOpen, 
  onClose,
  authorName,
  onConfirm
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const scrollLockRef = useRef(0);

  // Handle keyboard events and body scroll without losing page scroll position
  useEffect(() => {
    if (!isOpen) return;

    scrollLockRef.current = window.scrollY || window.pageYOffset || 0;

    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.top = originalStyle.top;
      document.body.style.width = originalStyle.width;
      window.scrollTo({ top: scrollLockRef.current || 0, behavior: "instant" });
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className="adoption-offer-confirmation-modal-overlay"
      onClick={handleBackdropClick}
    >
      <div 
        className="adoption-offer-confirmation-modal"
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="adoption-offer-confirmation-modal-header">
          <h2 className="adoption-offer-confirmation-modal-title">
            {t('adoption.modals.offerConfirmation.title') || 'Adoption Offer Sent'}
          </h2>
          <button
            className="adoption-offer-confirmation-modal-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="adoption-offer-confirmation-modal-content">
          <div className="adoption-offer-confirmation-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          
          <p className="adoption-offer-confirmation-message">
            {t('adoption.modals.offerConfirmation.message', { 
              authorName: authorName || 'the author' 
            }) || `Your adoption offer has been sent to ${authorName || 'the author'}!`}
          </p>
          
          <p className="adoption-offer-confirmation-privacy-note">
            {t('adoption.modals.offerConfirmation.privacyNote') || 'Please make sure your privacy settings allow users to message you to be able to contact them!'}
          </p>
        </div>

        {/* Footer */}
        <div className="adoption-offer-confirmation-modal-footer">
          <button
            className="adoption-offer-confirmation-modal-ok-btn"
            onClick={handleConfirm}
          >
            {t('adoption.modals.offerConfirmation.ok') || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AdoptionOfferConfirmationModal;

